/**
 * lineDiff — minimal line-level diff for the lore editor gutter.
 *
 * Compares two strings line-by-line using a simple LCS (Longest Common
 * Subsequence) algorithm and returns per-line change markers suitable for
 * rendering a git-style diff gutter. Designed for lore note sizes (hundreds
 * of lines at most), not megabyte diffs.
 *
 * Three marker types:
 *   'equal'   — line is unchanged between source and current
 *   'added'   — line exists in current but not in source (green gutter)
 *   'removed' — line existed in source but was deleted (red gutter, shown
 *               as a thin marker between current lines)
 *
 * The return value is an array of markers aligned to the CURRENT text's line
 * indices, with 'removed' entries inserted at the position where the deletion
 * occurred so the gutter can render a red bar without consuming a line number.
 */

export type DiffKind = 'equal' | 'added' | 'removed';

export interface DiffLine {
  kind: DiffKind;
  /** The line text (from current for equal/added, from source for removed). */
  text: string;
  /** 1-based line number in the CURRENT text. Null for 'removed' lines. */
  currentLineNo: number | null;
}

/**
 * Compute the LCS table for two arrays of strings.
 * Returns a 2D array where lcs[i][j] = length of LCS of a[0..i-1] and b[0..j-1].
 */
function lcsTable(a: string[], b: string[]): number[][] {
  const m = a.length;
  const n = b.length;
  // Use a flat approach for slightly better perf on larger inputs.
  const table: number[][] = Array.from({ length: m + 1 }, () =>
    new Array<number>(n + 1).fill(0),
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        table[i][j] = table[i - 1][j - 1] + 1;
      } else {
        table[i][j] = Math.max(table[i - 1][j], table[i][j - 1]);
      }
    }
  }
  return table;
}

/**
 * Backtrack through the LCS table to produce a diff sequence.
 */
function backtrack(
  table: number[][],
  a: string[],
  b: string[],
): Array<{ kind: DiffKind; text: string; fromA: boolean }> {
  const result: Array<{ kind: DiffKind; text: string; fromA: boolean }> = [];
  let i = a.length;
  let j = b.length;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      result.push({ kind: 'equal', text: a[i - 1], fromA: true });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || table[i][j - 1] >= table[i - 1][j])) {
      result.push({ kind: 'added', text: b[j - 1], fromA: false });
      j--;
    } else {
      result.push({ kind: 'removed', text: a[i - 1], fromA: true });
      i--;
    }
  }

  result.reverse();
  return result;
}

/**
 * Diff two strings line-by-line. Returns an array of DiffLine entries aligned
 * to the current (edited) text, with 'removed' markers inserted where lines
 * were deleted from the source.
 *
 * @param source  The published/baseline text.
 * @param current The current editor buffer text.
 */
export function lineDiff(source: string, current: string): DiffLine[] {
  const aLines = source.split('\n');
  const bLines = current.split('\n');

  const table = lcsTable(aLines, bLines);
  const raw = backtrack(table, aLines, bLines);

  const result: DiffLine[] = [];
  let lineNo = 0;

  for (const entry of raw) {
    if (entry.kind === 'equal') {
      lineNo++;
      result.push({ kind: 'equal', text: entry.text, currentLineNo: lineNo });
    } else if (entry.kind === 'added') {
      lineNo++;
      result.push({ kind: 'added', text: entry.text, currentLineNo: lineNo });
    } else {
      // 'removed' — no line number in the current text
      result.push({ kind: 'removed', text: entry.text, currentLineNo: null });
    }
  }

  return result;
}

/**
 * Simplified gutter data: for each line in the current text, what kind is it?
 * Also includes positions where removed lines should show a marker.
 */
export interface GutterEntry {
  kind: DiffKind;
  lineNo: number; // 1-based for equal/added; synthetic position for removed
}

/**
 * Compute gutter entries from a full diff. Each entry maps to a visual row
 * in the editor gutter.
 */
export function gutterFromDiff(diff: DiffLine[]): GutterEntry[] {
  return diff.map((d) => ({
    kind: d.kind,
    lineNo: d.currentLineNo ?? 0,
  }));
}
